import { useEffect, useRef, useState } from 'react';

/**
 * Live market data from Binance's PUBLIC streams (no API key, CORS-friendly).
 * Only market data is public here — order placement still goes through our backend.
 */
const WS_BASE = 'wss://stream.binance.com:9443';
const REST_BASE = 'https://api.binance.com';

export const TOP_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'ADAUSDT', 'DOGEUSDT', 'AVAXUSDT'];

export const KLINE_INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d'] as const;
export type KlineInterval = (typeof KLINE_INTERVALS)[number];

export interface TickerStat {
  symbol: string;
  price: number;
  changePct: number;
  high: number;
  low: number;
  quoteVol: number;
}

/** Multiplexed 24h ticker stream for several symbols at once. */
export function useBinanceTickers(symbols: string[]): Record<string, TickerStat> {
  const [data, setData] = useState<Record<string, TickerStat>>({});
  const key = symbols.join(',');

  useEffect(() => {
    if (!symbols.length) return;
    const streams = symbols.map((s) => `${s.toLowerCase()}@ticker`).join('/');
    const ws = new WebSocket(`${WS_BASE}/stream?streams=${streams}`);
    ws.onmessage = (ev) => {
      const d = JSON.parse(ev.data)?.data;
      if (!d?.s) return;
      setData((prev) => ({
        ...prev,
        [d.s]: {
          symbol: d.s, price: parseFloat(d.c), changePct: parseFloat(d.P),
          high: parseFloat(d.h), low: parseFloat(d.l), quoteVol: parseFloat(d.q),
        },
      }));
    };
    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return data;
}

/** Single-symbol live ticker (reuses the multiplexed hook). */
export function useBinanceTicker(symbol: string): TickerStat | null {
  const map = useBinanceTickers(symbol ? [symbol] : []);
  return symbol ? map[symbol.toUpperCase()] ?? null : null;
}

export interface BookLevel { price: number; qty: number; total: number; }
export interface OrderBookData { bids: BookLevel[]; asks: BookLevel[] }

/** Live partial order book (top N levels) via the @depth20 stream. */
export function useBinanceOrderBook(symbol: string, depth = 12): OrderBookData {
  const [book, setBook] = useState<OrderBookData>({ bids: [], asks: [] });

  useEffect(() => {
    if (!symbol) return;
    const ws = new WebSocket(`${WS_BASE}/ws/${symbol.toLowerCase()}@depth20@100ms`);
    const toLevels = (arr: [string, string][]): BookLevel[] => {
      let run = 0;
      return arr.slice(0, depth).map(([p, q]) => {
        const qty = parseFloat(q);
        run += qty;
        return { price: parseFloat(p), qty, total: run };
      });
    };
    ws.onmessage = (ev) => {
      const d = JSON.parse(ev.data);
      if (d?.bids && d?.asks) setBook({ bids: toLevels(d.bids), asks: toLevels(d.asks) });
    };
    return () => ws.close();
  }, [symbol, depth]);

  return book;
}

export interface Candle { time: number; open: number; high: number; low: number; close: number; }

/** Historical klines (REST) kept live via the @kline stream. */
export function useBinanceKlines(symbol: string, interval: KlineInterval = '1m', limit = 120): Candle[] {
  const [candles, setCandles] = useState<Candle[]>([]);
  const symRef = useRef(symbol);
  symRef.current = symbol;

  useEffect(() => {
    if (!symbol) return;
    let active = true;
    setCandles([]);
    fetch(`${REST_BASE}/api/v3/klines?symbol=${symbol.toUpperCase()}&interval=${interval}&limit=${limit}`)
      .then((r) => r.json())
      .then((rows: unknown) => {
        if (!active || !Array.isArray(rows)) return;
        setCandles(rows.map((k) => ({ time: k[0], open: +k[1], high: +k[2], low: +k[3], close: +k[4] })));
      })
      .catch(() => undefined);

    const ws = new WebSocket(`${WS_BASE}/ws/${symbol.toLowerCase()}@kline_${interval}`);
    ws.onmessage = (ev) => {
      const k = JSON.parse(ev.data)?.k;
      if (!k) return;
      const c: Candle = { time: k.t, open: +k.o, high: +k.h, low: +k.l, close: +k.c };
      setCandles((prev) => {
        if (!prev.length) return prev;
        const last = prev[prev.length - 1];
        if (last.time === c.time) return [...prev.slice(0, -1), c];
        if (c.time > last.time) return [...prev.slice(-limit + 1), c];
        return prev;
      });
    };
    return () => { active = false; ws.close(); };
  }, [symbol, interval, limit]);

  return candles;
}

/** Compact price formatter that adapts decimals to magnitude. */
export function fmtPrice(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(2);
  return n.toFixed(5);
}
